import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { WINDOW } from '@pin-up-global/common';
import { Observable } from 'rxjs';
import { getDecodedUrl, getEncodedUrl } from '@shared/utils';
import { AccessData } from '../auth.abstraction';
import { STANDALONE_AUTH_TOKEN_KEY, STANDALONE_REFRESH_TOKEN_KEY } from '../auth.constant';
import { AuthGrpcApiService } from '../auth-grpc-api.service';

/**
 * This service should be included in standalone.ts.
 * This service is used for standalone gRPC authentication.
 */
@Injectable()
export class StandaloneGrpcAuthUtilityService {
  #router = inject(Router);
  #window = inject(WINDOW);
  #authApiService = inject(AuthGrpcApiService);

  refresh(): Observable<Omit<AccessData, 'refresh'>> {
    return this.#authApiService.refresh(this.getRefreshToken());
  }

  getSignInUrlTree(): UrlTree {
    const { pathname } = this.#window.location;
    const url = !pathname || pathname === '/' || pathname.includes('signin')
        ? `/signin-grpc`
        : `/signin-grpc?url=${ getEncodedUrl(pathname) }`;

    return this.#router.parseUrl(url);
  }

  getPostSignInUrlTree(): UrlTree {
    const params = new URLSearchParams(this.#window.location.search);
    const encodedUrl = params.get('url');
    const decodedUrl = encodedUrl ? getDecodedUrl(encodedUrl) : null;

    const url = decodedUrl && !decodedUrl.includes('signin')
        ? (decodedUrl.startsWith('/') ? decodedUrl : `/${ decodedUrl }`)
        : '/';

    return this.#router.parseUrl(url);
  }

  getAccessToken(): string | null {
    return this.#window.localStorage.getItem(STANDALONE_AUTH_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.#window.localStorage.getItem(STANDALONE_REFRESH_TOKEN_KEY);
  }

  removeTokens(): void {
    this.#window.localStorage.removeItem(STANDALONE_AUTH_TOKEN_KEY);
    this.#window.localStorage.removeItem(STANDALONE_REFRESH_TOKEN_KEY);
  }

  saveTokens(access: string, refresh?: string): void {
    this.#window.localStorage.setItem(STANDALONE_AUTH_TOKEN_KEY, access);

    if (refresh) {
      this.#window.localStorage.setItem(STANDALONE_REFRESH_TOKEN_KEY, refresh);
    }
  }
}
